import { cva, type VariantProps } from "class-variance-authority";

import { InfoIcon } from "@hero-app/components/ui/icons/nav-icons";
import { cn } from "@hero-app/lib/utils";
import { TrendBadge } from "./trend-badge";

// StatCard supports orthogonal axes:
//  - size    sm | md | lg   → scale (padding + typography)
//  - status  default | success | warning → semantic border/bg/value tint
//  - compact boolean        → 2-row dense layout (label+trend / value+total)
//  - chip    boolean        → single-line layout (label · value · total · trend)
// Precedence: chip > compact > default.
// Defaults (md / default / non-compact) reproduce the original card exactly.
//
// This module is intentionally the ONLY component in its file: the manifest
// generator's CVA detector attributes a file's lone cva() config to every
// component it finds in that file, so co-locating other components here would
// make them falsely advertise StatCard's size/status variants.
const statCardVariants = cva("min-w-0 flex-1 rounded-card border-[0.5px]", {
  variants: {
    status: {
      default: "border-[#e3e6e8] bg-[#FFFFFF80]",
      success: "border-[#16a34a]/30 bg-[#16a34a]/[0.06]",
      warning: "border-[#d97706]/30 bg-[#d97706]/[0.06]",
    },
    size: {
      sm: "p-2",
      md: "p-3",
      lg: "p-4",
    },
  },
  defaultVariants: { status: "default", size: "md" },
});

export type StatCardStatus = NonNullable<VariantProps<typeof statCardVariants>["status"]>;
export type StatCardSize = NonNullable<VariantProps<typeof statCardVariants>["size"]>;

const STAT_CARD_TYPE: Record<StatCardSize, { value: string; label: string; total: string }> = {
  sm: { value: "text-[18px]", label: "text-[10px]", total: "text-[10px]" },
  md: { value: "text-[26px]", label: "text-[11px]", total: "text-[11px]" },
  lg: { value: "text-[34px]", label: "text-[12px]", total: "text-[12px]" },
};

const STAT_CARD_VALUE_COLOR: Record<StatCardStatus, string> = {
  default: "text-[#1a2026]",
  success: "text-[#16a34a]",
  warning: "text-[#d97706]",
};

export function StatCard({
  label,
  value,
  total,
  trend,
  pos,
  size = "md",
  status = "default",
  compact = false,
  chip = false,
}: {
  label: string;
  value: string;
  total: string;
  trend: string | null;
  pos: boolean | null;
  size?: StatCardSize;
  status?: StatCardStatus;
  compact?: boolean;
  chip?: boolean;
}) {
  const type = STAT_CARD_TYPE[size];
  const valueColor = STAT_CARD_VALUE_COLOR[status];
  const totalRow = (
    <span className={cn("inline-flex items-center gap-0.5 text-black/30", type.total)}>
      {total}
      <InfoIcon className="size-2.5 shrink-0" />
    </span>
  );
  const trendBadge = trend && pos !== null ? <TrendBadge value={trend} positive={pos} /> : null;

  if (chip) {
    return (
      <div
        className={cn(
          statCardVariants({ size, status }),
          "flex items-center gap-1.5 px-2.5 py-1",
        )}
      >
        <span className="min-w-0 flex-1 truncate text-[11px] text-black/30">{label}</span>
        <span
          className={cn(
            "shrink-0 text-[13px] font-medium leading-none tracking-[-0.4px]",
            valueColor,
          )}
        >
          {value}
        </span>
        <span className="shrink-0 text-[11px] text-black/30">{total}</span>
        {trendBadge}
      </div>
    );
  }

  if (compact) {
    return (
      <div className={statCardVariants({ size, status })}>
        <div className="flex items-center justify-between gap-1.5">
          <p className={cn("min-w-0 truncate text-black/30", type.label)}>{label}</p>
          {trendBadge}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-1.5">
          <span className={cn("leading-none tracking-[-1px]", type.value, valueColor)}>{value}</span>
          {totalRow}
        </div>
      </div>
    );
  }

  return (
    <div className={statCardVariants({ size, status })}>
      <p className={cn("text-black/30", type.label)}>{label}</p>
      <p className={cn("mt-2 leading-none tracking-[-1px]", type.value, valueColor)}>{value}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {totalRow}
        {trendBadge}
      </div>
    </div>
  );
}
